/**
 * CloudImpostorLayer — the FAR LOD of the mesh-cloud field (2026-09-10,
 * "add a LOD system for clouds so clouds can appear far away").
 *
 * The volumetric field marches real 3D volumes, so it can only afford a few
 * kilometres around the camera; beyond its fade band the sky used to be
 * EMPTY (the field radius was sized for a 4 km far plane the terrain has
 * since pushed to 120 km). This layer continues the SAME weather lattice out
 * to `farRadius` with instanced camera-facing sprites — the Harris (2001)
 * doctrine that impostors suit clouds better than anything else, as
 * flight simulators have done for decades (MSFS 2004: billboards near,
 * impostors far) — every sprite standing for the cloud its cell would grow
 * into: the same cell hash gives the same place, size, character, seed and
 * turret layout (`deriveCloudCell`), so the hand-off between the field's
 * fade-out and the sprite's fade-in swaps two pictures of ONE cloud. Past
 * `midRadius` a coarser lattice (cells × farCellScale) carries clusters to
 * the horizon.
 *
 * The sprite is procedural — no atlas: the cloud's turret lobes projected
 * onto the view plane as soft discs (their spread turns with the viewer's
 * bearing, so a cloud shows a different profile from each side), a slice of
 * the shared 3D noise for the cauliflower texture and edge erosion, a
 * per-pixel normal from the nearest lobe (the "bent normal" billboard trick)
 * for sun wrap lighting, the sky-tinted ambient gradient of the field, a
 * silver lining when the sun stands behind the cloud, Beer-law alpha from
 * the accumulated lobe thickness, and the scene's own exponential fog as
 * aerial perspective so the far clouds sink into the haze the terrain does.
 * Same premultiplied OVER contract as the field, drawn before it
 * (renderOrder) so near volumes composite over far sprites.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../post/mrtOverlay.js';
import {
  Fn, uniform, float, vec2, vec3, vec4, attribute, varying,
  max, min, mix, normalize, length, smoothstep, cross,
  cameraPosition, positionLocal, positionPrevious, positionWorld,
} from 'three/tsl';
import {
  CLOUD_ARCHETYPES, coverageThreshold, deriveCloudCell, sampleWeatherCpu,
  type CloudMeshField, type CloudLayerSpec, type CloudArchetype, type CloudImpostorLayerConfig,
} from './CloudMeshField.js';
import { MAX_LOBES } from './CloudLobes.js';
import { buildCloudMarch, cloudAir } from './CloudMarch.js';

/** The far form's march: samples per fragment, sun-cone samples, and the first step's stretch (the field's `lod`). */
const FAR_STEPS = 12;
const FAR_SUN_STEPS = 3;
const FAR_LOD = 2.5;
/** A card fades in between 2.4 and 3.2 of its dome's bounding radii: nearer, the camera is too close to (or inside) the
 *  sphere the card stands for. Every ordinary cloud is far past it (≈ 1 km against the field's 3.4 km hand-off); a
 *  far-only giant is streamed from GIANT_NEAR radii of its RADIUS out. */
const GIANT_FADE_IN = 2.4, GIANT_FADE_FULL = 3.2, GIANT_NEAR = 2.6;

export interface ImpostorCell {
  key: string;
  cx: number; cz: number;
  cellSize: number;
  /** 0 = the field's lattice (mid band), 1 = the coarse far lattice. */
  band: 0 | 1;
  dist: number;
  spec: CloudLayerSpec;
  arch: CloudArchetype;
  layerIndex: number;
}

/**
 * Cells that should carry a sprite (pure, unit-tested): per étage, the
 * field's lattice from `innerRadius` (the field's fade start) to
 * `midRadius`, then the coarse lattice to `farRadius`, each passing the
 * layer's coverage threshold on the weather map. Sorted FAR-FIRST (the draw
 * order — an instanced mesh cannot depth-sort its instances), capped by
 * dropping the farthest.
 */
export function computeImpostorCells(
  camX: number, camZ: number, coverage: number, formation: CloudLayerSpec[],
  opts: { innerRadius: number; midRadius: number; farRadius: number; cellSize: number; farCellScale: number; maxSprites: number; weatherScale: number },
  weather: { data: Uint8Array; size: number },
): ImpostorCell[] {
  const out: ImpostorCell[] = [];
  formation.forEach((spec, li) => {
    const arch = CLOUD_ARCHETYPES[spec.kind];
    const layerCov = Math.max(0, Math.min(1, coverage * spec.coverage));
    if (layerCov < 0.02) return;
    const threshold = coverageThreshold(layerCov, coverage); // THE OVERCAST FLOOR: the global cover fades the 0.25 floor
    const off = { x: li * 3777.7, z: li * -2444.3 }; // the field's per-étage decorrelation
    const bands: Array<{ band: 0 | 1; cell: number; r0: number; r1: number }> = [
      { band: 0, cell: opts.cellSize * arch.cellScale, r0: opts.innerRadius, r1: opts.midRadius * 1.08 },
      { band: 1, cell: opts.cellSize * arch.cellScale * opts.farCellScale, r0: opts.midRadius * 0.92, r1: opts.farRadius },
    ];
    for (const b of bands) {
      const ccx = Math.floor(camX / b.cell), ccz = Math.floor(camZ / b.cell);
      const range = Math.ceil(b.r1 / b.cell);
      for (let dx = -range; dx <= range; dx++) {
        for (let dz = -range; dz <= range; dz++) {
          const cx = ccx + dx, cz = ccz + dz;
          const wx = (cx + 0.5) * b.cell, wz = (cz + 0.5) * b.cell;
          const dist = Math.hypot(wx - camX, wz - camZ);
          // A GIANT stands off: no card nearer than its fade-in (GIANT_NEAR bounding radii — see the fragment's giantFade).
          if (dist < Math.max(b.r0, arch.farOnly ? arch.radiusMax * GIANT_NEAR : 0) || dist > b.r1) continue;
          const w = sampleWeatherCpu(weather.data, weather.size, wx + off.x, wz + off.z, opts.weatherScale);
          if (w.r < threshold) continue;
          out.push({ key: `I${b.band}L${li}:${cx},${cz}`, cx, cz, cellSize: b.cell, band: b.band, dist, spec, arch, layerIndex: li });
        }
      }
    }
  });
  out.sort((a, b) => b.dist - a.dist); // far first
  if (out.length > opts.maxSprites) {
    // drop the farthest — but never a GIANT (a storm on the horizon is the farthest thing in the sky and the last to go)
    let drop = out.length - opts.maxSprites;
    for (let i = 0; i < out.length && drop > 0;) { if (out[i].arch.farOnly) i++; else { out.splice(i, 1); drop--; } }
  }
  return out;
}

export class CloudImpostorLayer {
  readonly group = new THREE.Group();
  private readonly _field: CloudMeshField;
  private readonly _cfg: Required<CloudImpostorLayerConfig>;
  /** A plain Mesh over an InstancedBufferGeometry — never an InstancedMesh (THE VERTEX-LOCATION LAW, the constructor). */
  private readonly _mesh: THREE.Mesh;
  /**
   * Per-instance data, ALL IN ONE interleaved buffer.
   *
   * WHY (2026-09-15): these were ten separate `InstancedBufferAttribute`s —
   * iCenter, iShape, iSeed and seven lobes. WebGPU allows 8 VERTEX BUFFERS
   * (`maxVertexBuffers`) and three binds one per attribute the shader reads, so
   * with position/normal/uv and the instance matrix this layer asked for 14 and
   * `createRenderPipeline` refused it outright:
   *
   *     Vertex buffer count (13) exceeds the maximum number of vertex buffers (8)
   *
   * The pipeline then stayed invalid forever, and because an invalid pipeline
   * poisons the command buffer it was encoded into, EVERY frame's submit failed
   * — an unbounded console flood and a renderer doing work it could never
   * present. (WebGL2 counts attribute LOCATIONS, not buffers, which is why this
   * only ever broke on the primary backend.)
   *
   * three keys its vertex-buffer map by the underlying buffer
   * (`WebGPUAttributeUtils.createShaderVertexBuffers`), so interleaved
   * attributes sharing one `InstancedInterleavedBuffer` cost ONE buffer no
   * matter how many of them there are. Ten attributes → one. The layer now
   * binds four: position, normal, uv and this (no instance matrix — see THE
   * VERTEX-LOCATION LAW in the constructor).
   */
  private readonly _instanceData: THREE.InstancedInterleavedBuffer;
  private readonly _attrs: Record<string, THREE.InterleavedBufferAttribute>;
  /** Float offset of each vec4 slot within one instance's stride. */
  private readonly _slot: Record<string, number> = {};
  private readonly _uMidRadius: ReturnType<typeof uniform>;
  private readonly _uFarRadius: ReturnType<typeof uniform>;
  private _enabled = true;
  private _lastCellX = Number.NaN;
  private _lastCellZ = Number.NaN;
  private _lastCov = -1;
  private _lastFormation: CloudLayerSpec[] | null = null;
  private _lastUplightGen = -1;
  private _active = 0;
  private readonly _up = { x: 0, y: 0, z: 0 };

  constructor(opts: { field: CloudMeshField; config?: CloudImpostorLayerConfig }) {
    this._field = opts.field;
    const c = opts.config ?? {};
    this._cfg = {
      midRadius: c.midRadius ?? 16000,
      farRadius: c.farRadius ?? 60000,
      farCellScale: c.farCellScale ?? 4,
      maxSprites: c.maxSprites ?? 1600,
    };
    this._uMidRadius = uniform(this._cfg.midRadius);
    this._uFarRadius = uniform(this._cfg.farRadius);
    const n = this._cfg.maxSprites;
    // THE VERTEX-LOCATION LAW (2026-09-26, the owner's tier-high report): WebGPU enforces maxVertexAttributes 16 as
    // well as maxVertexBuffers 8 — a LOCATION per attribute the shader reads. An InstancedMesh costs 4 locations
    // for `instanceMatrix` and, whenever the scene pass carries velocity (TAAU — the high/ultra default — or the
    // motion blur), 4 more for three's previous-frame matrix (`Instance.js`, added material or not). With the
    // quad's three and the ten vec4 slots below that made 21, and Dawn refused the pipeline
    // ("nodeAttribute4..7 … location (16..19) exceeds the maximum (16)"), poisoning every frame's submit. This
    // layer never reads the instance matrix (the sprite is placed from iCenter in the vertex stage), so it draws a
    // plain Mesh over an InstancedBufferGeometry: 13 locations and 4 buffers on either backend, velocity MRT or not.
    const quad = new THREE.PlaneGeometry(2, 2, 1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.setIndex(quad.getIndex()); // the quad's index + position/normal/uv, shared (never uploaded under `quad`)
    for (const name of ['position', 'normal', 'uv']) geo.setAttribute(name, quad.getAttribute(name));
    geo.instanceCount = 0;
    // One buffer, eleven vec4 slots per instance — see _instanceData. `iUplight` (2026-10-06, the night-sky realism wave):
    // the ground's light under the cloud (rgb, the field's provider units) — one more LOCATION (14 of 16), no new buffer.
    const names = ['iCenter', 'iShape', 'iSeed', ...Array.from({ length: MAX_LOBES }, (_, i) => `iLobe${i}`), 'iUplight'];
    const stride = names.length * 4;
    this._instanceData = new THREE.InstancedInterleavedBuffer(new Float32Array(n * stride), stride);
    // THE DYNAMIC-USAGE LAW (the shader sweep, 2026-10-06): three r185 re-uploads a DynamicDraw attribute on EVERY render call that draws it (each shadow cascade, the scene pass, a reflection) and the first call consumes the update range — later calls sent the WHOLE capacity. The buffer is rewritten only on a re-stream, and that write bumps the version, so Static usage uploads once a frame.
    this._attrs = {};
    names.forEach((name, k) => {
      this._slot[name] = k * 4;
      const a = new THREE.InterleavedBufferAttribute(this._instanceData, 4, k * 4);
      geo.setAttribute(name, a);
      this._attrs[name] = a;
    });
    const material = this._buildMaterial();
    this._mesh = new THREE.Mesh(geo, material);
    this._mesh.frustumCulled = false; // positions are computed in the vertex stage
    this._mesh.renderOrder = 9; // before the volumetric meshes (10): far sprites under near volumes
    this._mesh.name = 'CloudImpostors';
    this.group.add(this._mesh);
    this.group.name = 'CloudImpostorLayer';
  }

  get enabled(): boolean { return this._enabled; }
  setEnabled(on: boolean): void { this._enabled = on; this.group.visible = on; }
  get activeCount(): number { return this._active; }
  get config(): Required<CloudImpostorLayerConfig> { return this._cfg; }

  /** Aerial perspective: the scene fog's colour + FogExp2 density (squared law). */
  setHaze(color: { r: number; g: number; b: number }, density: number): void {
    this._field.setHaze(color, density); // one air for the volumes and the far layer (CloudMarch.cloudAir)
  }

  /** Per-frame drive (render-space cam + scene position, like the field). */
  update(_dt: number, cam: THREE.Vector3, scenePos: THREE.Vector3): void {
    if (!this._enabled) return;
    // THE RENDER-SPACE CARD (2026-10-07): the vertex stage places each card in RENDER space (the cloud's absolute base
    // + the field's worldOffset = scene.position), and three then applies this mesh's world matrix — the SCENE's
    // rebase shift again. Wherever the origin had rebased (anywhere past 10 km from the world origin: every real
    // player) the whole far layer was drawn a rebase away, i.e. never: the sky ended at the field's 4.2 km. The group
    // cancels the scene's shift, so its world matrix is the identity and the card's local position IS render space.
    this.group.position.set(-scenePos.x, -scenePos.y, -scenePos.z);
    const camAbsX = cam.x - scenePos.x, camAbsZ = cam.z - scenePos.z;
    const sh = this._field.shared;
    const cellX = Math.floor(camAbsX / sh.cfg.cellSize), cellZ = Math.floor(camAbsZ / sh.cfg.cellSize);
    const covBucket = Math.round(sh.coverage / 0.04);
    if (cellX !== this._lastCellX || cellZ !== this._lastCellZ || covBucket !== this._lastCov || sh.formation !== this._lastFormation || sh.uplightGen !== this._lastUplightGen) {
      this._lastCellX = cellX; this._lastCellZ = cellZ; this._lastCov = covBucket; this._lastFormation = sh.formation; this._lastUplightGen = sh.uplightGen;
      this._restream(camAbsX, camAbsZ);
    }
  }

  private _restream(camAbsX: number, camAbsZ: number): void {
    const sh = this._field.shared;
    const cells = computeImpostorCells(camAbsX, camAbsZ, sh.coverage, sh.formation, {
      innerRadius: sh.cfg.fieldRadius * sh.cfg.fadeStartRatio,
      midRadius: this._cfg.midRadius, farRadius: this._cfg.farRadius,
      cellSize: sh.cfg.cellSize, farCellScale: this._cfg.farCellScale,
      maxSprites: this._cfg.maxSprites, weatherScale: sh.cfg.weatherScale,
    }, sh.weather);
    const data = this._instanceData;
    const arr = data.array as Float32Array;
    const stride = data.stride;
    const slot = this._slot;
    const put = (name: string, i: number, x: number, y: number, z: number, w: number): void => {
      const o = i * stride + slot[name];
      arr[o] = x; arr[o + 1] = y; arr[o + 2] = z; arr[o + 3] = w;
    };
    cells.forEach((c, i) => {
      const cell = deriveCloudCell(c.cx, c.cz, c.cellSize, c.spec, c.arch, sh.weather, sh.cfg.weatherScale);
      // A coarse far cell stands for a cluster: grow the sprite with the lattice.
      const sizeMul = c.band === 1 ? Math.sqrt(this._cfg.farCellScale) * 0.9 : 1;
      put('iCenter', i, cell.absX, cell.shape.baseY, cell.absZ, cell.shape.radius * sizeMul);
      put('iShape', i, cell.shape.height * sizeMul, cell.params.x, cell.params.y, c.band);
      put('iSeed', i, cell.seed.x, cell.seed.y, cell.seed.z, cell.type01);
      for (let l = 0; l < MAX_LOBES; l++) { const lb = cell.lobes[l]; put(`iLobe${l}`, i, lb.x, lb.y, lb.z, lb.r); }
      // THE UPLIGHT (2026-10-06): the same provider as the volumes, at the sprite's cell — a cloud over a far town glows
      // from below, one over the wilds does not (the hand-off swaps two pictures of ONE cloud, lit alike).
      const up = this._field.uplightAt(cell.absX, cell.absZ, this._up);
      put('iUplight', i, up.x, up.y, up.z, 0);
    });
    // ONE buffer to flag now: the rows written are contiguous from 0.
    data.needsUpdate = true;
    data.addUpdateRange(0, cells.length * stride);
    (this._mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = cells.length;
    this._active = cells.length;
  }

  dispose(): void {
    (this._mesh.material as THREE.Material).dispose();
    // geometry.dispose() releases every attribute the renderer uploaded for it — the quad's and the interleaved
    // instance data (iCenter/iShape/iSeed/iLobe*, 1600 rows in ONE buffer; three keys the interleaved ones by
    // their shared `data`); a plain Mesh has no dispose of its own. The group CloudModule scene.add()ed must leave
    // the scene or every teardown strands an orphan Group (2026-09-12).
    this._mesh.geometry.dispose();
    this.group.clear();
    this.group.removeFromParent();
  }

  private _buildMaterial(): THREE.NodeMaterial {
    const sh = this._field.shared;
    const S = sh.uniforms;
    const uWorldOffset = sh.worldOffset;
    const uFadeStart = sh.fadeStart, uFadeEnd = sh.fadeEnd;
    const uMid = this._uMidRadius, uFar = this._uFarRadius;

    const iCenter = attribute('iCenter', 'vec4');
    const iShape = attribute('iShape', 'vec4');
    const iSeed = attribute('iSeed', 'vec4');
    const iUplight = attribute('iUplight', 'vec4');
    const lobeAttrs = Array.from({ length: MAX_LOBES }, (_, i) => attribute(`iLobe${i}`, 'vec4'));

    // ── Vertex: a camera-facing card that covers the dome's BOUNDING SPHERE (centre half-way up, radius to the base
    // rim), grown by d/√(d² − ρ²) so the card in the centre plane spans the sphere's whole silhouette. (The old card
    // stood on the base point and rose 1.15 heights: fine for a painted profile, but the march needs every ray that
    // meets the dome — from below, the far rim of the base drops under the base point.)
    const vDist = varying(float(0), 'vDist');
    const positionNode = Fn(() => {
      const halfH = iShape.x.mul(0.5);
      const cen = iCenter.xyz.add(uWorldOffset).add(vec3(0, halfH, 0));
      const rho = iCenter.w.mul(iCenter.w).add(halfH.mul(halfH)).sqrt().mul(1.06);
      const toCam = cameraPosition.sub(cen);
      const dist = length(toCam);
      const n = toCam.div(max(dist, float(1)));
      // Right axis = up × n, guarded near the zenith.
      const flatLen = length(vec2(n.x, n.z));
      const rightRaw = vec3(n.z, 0, n.x.negate()).div(max(flatLen, float(1e-4)));
      const right = mix(vec3(1, 0, 0), rightRaw, smoothstep(float(0.02), float(0.12), flatLen));
      const up = cross(n, right);
      const corner = positionLocal.xy;
      const grow = min(dist.div(max(dist.mul(dist).sub(rho.mul(rho)), dist.mul(dist).mul(0.01)).sqrt()), float(3));
      vDist.assign(dist);
      const placed = cen.add(right.mul(corner.x)).add(up.mul(corner.y)).sub(cen).mul(rho.mul(grow)).add(cen);
      // Motion vectors: the previous position is this same billboard (a
      // camera-facing card has no object motion of its own); without it
      // three's default previous — the un-placed quad — read as metres of
      // velocity per frame and the motion blur streaked every distant cloud.
      positionPrevious.assign(placed);
      return placed;
    })();

    // ── Fragment: THE SAME CLOUD AS THE VOLUMES (2026-10-07). The card used to paint the turrets as shaded discs with a
    // slice of noise — bunches of grey balls along the horizon under a sky of lit heaps, and a cloud that changed its
    // picture as it crossed the hand-off. It now runs the field's own march (CloudMarch) on the same per-cloud data,
    // with the far form's economy: FAR_STEPS samples, one noise fetch each, FAR_SUN_STEPS toward the sun. Same turrets,
    // same florets, same light — a flat grey base and a sunlit side at 30 km as at 300 m.
    const env = this._field.marchEnv;
    const cm = buildCloudMarch({
      center: iCenter.xyz, radius: iCenter.w, height: iShape.x,
      params: vec3(iShape.y, iShape.z, 0), seed: iSeed.xyz, lobe: (i: number) => lobeAttrs[i], uplight: iUplight.xyz,
      condense: float(0), lod: float(FAR_LOD),
    }, env, { steps: FAR_STEPS, sunSteps: FAR_SUN_STEPS, cheap: true });
    const colorNode = Fn(() => {
      const ro = cameraPosition;
      const rd = normalize(positionWorld.sub(ro));
      const dist = vDist;
      // LOD fades: in over the field's fade band (where its volumes fade
      // out), the mid/far lattice hand-off around midRadius, out at farRadius.
      const nearFade = smoothstep(uFadeStart, uFadeEnd, dist);
      const midT = smoothstep(uMid.mul(0.92), uMid.mul(1.08), dist);
      const bandFade = mix(float(1).sub(midT), midT, iShape.w);
      const farFade = float(1).sub(smoothstep(uFar.mul(0.85), uFar, dist));
      const covFade = smoothstep(float(0.02), float(0.08), S.coverage);
      const halfHf = iShape.x.mul(0.5);
      const rhoF = iCenter.w.mul(iCenter.w).add(halfHf.mul(halfHf)).sqrt();
      const giantFade = smoothstep(rhoF.mul(GIANT_FADE_IN), rhoF.mul(GIANT_FADE_FULL), dist);
      const fade = nearFade.mul(bandFade).mul(farFade).mul(covFade).mul(giantFade);
      const r = cm.march(ro, rd, fade.greaterThan(float(0.002)));
      // THE CLOUDS' AIR (CloudMarch.cloudAir): the clear air's extinction and light per channel + the scene's fog, so a
      // far cloud sinks into the same haze as the terrain (premultiplied: the air takes the cloud's own opacity).
      const rgb = cloudAir(env, r.scat, r.alpha, dist, ro.y, rd.y);
      return vec4(rgb.mul(fade), r.alpha.mul(fade)); // premultiplied
    })();

    const material = new THREE.NodeMaterial();
    material.positionNode = positionNode as never;
    material.colorNode = colorNode as never;
    material.side = THREE.DoubleSide;
    material.transparent = true;
    material.depthWrite = false;
    material.fog = false; // the sprite applies the fog law itself (see haze)
    material.blending = THREE.CustomBlending;
    material.blendSrc = THREE.OneFactor;
    material.blendDst = THREE.OneMinusSrcAlphaFactor;
    markMrtOverlay(material); // a sprite over the horizon leaves the ground's normal alone (AO)
    return material;
  }
}
